// RestoMag Events — field-set schema (SHARED: React form + submit-lead Edge Function)
// The single source of truth for what a valid event field set is, and for building
// the per-event lead-values validator. Imported by both runtimes via the bare "zod"
// specifier (Vite resolves node_modules; the Edge Function maps it via import_map.json).
// See docs/ARCHITECTURE.md §6 and §9.
import { z } from "zod";

export const FIELD_TYPES = ["text", "email", "tel", "select", "textarea"] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

// Reserved core keys with fixed semantics across all events (PRD §5 / Q6).
export const RESERVED_CORE_KEYS = ["full_name", "email", "phone"] as const;

const DEFAULT_MAX_LEN = 500;
const NAME_MAX_LEN = 100;
const EMAIL_MAX_LEN = 254; // RFC 5321
const PHONE_MAX_LEN = 40;
const PHONE_MIN_DIGITS = 10; // NANP local number
const PHONE_MAX_DIGITS = 15; // E.164 ceiling (incl. country code)

// Reject control characters / NUL bytes as defense-in-depth. NUL bytes can't even be
// stored in Postgres text, and stripping control chars closes a class of injection /
// log-forging / display-spoofing payloads before they ever reach the DB.
const CONTROL_SINGLE_LINE = new RegExp("[\u0000-\u001F\u007F]"); // any control char (incl. tab / newline / CR)
const CONTROL_MULTILINE = new RegExp("[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]"); // control chars except tab / newline / CR
const NO_CONTROL = "Contains invalid characters";

/** Count just the digits in a phone string (ignores +, (), -, spaces, dots). */
function phoneDigitCount(s: string): number {
  return (s.match(/\d/g) ?? []).length;
}

/** Blank / whitespace-only optional value → undefined (so optional() passes). */
function blankToUndefined(v: unknown): unknown {
  return typeof v === "string" && v.trim() === "" ? undefined : v;
}

/** One field definition stored in events.events.field_set (jsonb). */
export const FieldDefSchema = z
  .object({
    key: z
      .string()
      .regex(/^[a-z][a-z0-9_]*$/, "key must be snake_case (a-z, 0-9, _)"),
    label: z.string().min(1),
    type: z.enum(FIELD_TYPES),
    required: z.boolean().default(false),
    options: z.array(z.string().min(1)).optional(),
    maxLength: z.number().int().positive().max(5000).optional(),
  })
  .superRefine((f, ctx) => {
    if (f.type === "select" && (!f.options || f.options.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "select fields require a non-empty options array",
        path: ["options"],
      });
    }
  });
export type FieldDef = z.infer<typeof FieldDefSchema>;

/** The whole field set: unique keys + at least one contact handle. */
export const FieldSetSchema = z
  .array(FieldDefSchema)
  .superRefine((fields, ctx) => {
    const keys = fields.map((f) => f.key);
    if (new Set(keys).size !== keys.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "duplicate field keys" });
    }
    const hasContact = fields.some(
      (f) => f.type === "email" || f.type === "tel" || f.key === "email" || f.key === "phone",
    );
    if (!hasContact) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "a field set must contain at least one email or phone field",
      });
    }
  });
export type FieldSet = z.infer<typeof FieldSetSchema>;

/** Is this a name-style field? Names get a tighter length cap (but no character
 *  allowlist — name regexes wrongly reject valid international / accented names). */
function isNameKey(key: string): boolean {
  return key === "full_name" || key === "name";
}

/** Build the Zod validator for a single field's submitted value. */
function fieldValueSchema(f: FieldDef): z.ZodTypeAny {
  // select: closed set of options — z.enum already rejects "" and unknown values.
  if (f.type === "select") {
    const base = z.enum((f.options ?? [""]) as [string, ...string[]]);
    return f.required ? base : z.preprocess(blankToUndefined, base.optional());
  }

  // Build the string validator with all type-specific ZodString constraints first,
  // then apply the required/optional + refinement layers (refine → ZodEffects).
  let str = z.string().trim();
  switch (f.type) {
    case "email":
      str = str.toLowerCase().email(`${f.label} must be a valid email`).max(EMAIL_MAX_LEN);
      break;
    case "tel":
      str = str.max(PHONE_MAX_LEN).regex(/^[0-9+()\-.\s]+$/, "Enter a valid phone number");
      break;
    case "textarea":
      str = str.max(f.maxLength ?? DEFAULT_MAX_LEN);
      break;
    case "text":
    default:
      str = str.max(f.maxLength ?? (isNameKey(f.key) ? NAME_MAX_LEN : DEFAULT_MAX_LEN));
      break;
  }

  if (f.required) str = str.min(1, `${f.label} is required`);

  // Defense-in-depth + real-format refinements (these turn the schema into ZodEffects).
  let validated: z.ZodTypeAny = str;
  if (f.type === "tel") {
    validated = str.refine(
      (s) => phoneDigitCount(s) >= PHONE_MIN_DIGITS && phoneDigitCount(s) <= PHONE_MAX_DIGITS,
      "Enter a valid phone number",
    );
  } else if (f.type === "textarea") {
    validated = str.refine((s) => !CONTROL_MULTILINE.test(s), NO_CONTROL);
  } else if (f.type === "text") {
    validated = str.refine((s) => !CONTROL_SINGLE_LINE.test(s), NO_CONTROL);
  }
  // email: z.email() already rejects whitespace/control chars, so no extra refine.

  if (f.required) return validated;
  // optional: blank / whitespace-only normalizes to undefined so optional() passes.
  return z.preprocess(blankToUndefined, validated.optional());
}

/**
 * Build the strict object validator for a lead's `field_values` from the event's
 * field set. Used by the React form (RHF resolver) AND re-run server-side in the
 * Edge Function. `.strict()` rejects any key not declared in the field set.
 */
export function buildFieldValuesSchema(rawFieldSet: unknown) {
  const fieldSet = FieldSetSchema.parse(rawFieldSet);
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of fieldSet) shape[f.key] = fieldValueSchema(f);
  return z.object(shape).strict();
}

/** Extract + normalize the contact handles for the first-class leads columns. */
export function extractContact(
  fieldSet: FieldSet,
  values: Record<string, unknown>,
): { email: string | null; phone: string | null } {
  let email: string | null = null;
  let phone: string | null = null;
  for (const f of fieldSet) {
    const val = values[f.key];
    if (typeof val !== "string" || val.trim() === "") continue;
    if (f.type === "email" || f.key === "email") email = val.trim().toLowerCase();
    if (f.type === "tel" || f.key === "phone") phone = val.replace(/[^\d+]/g, "");
  }
  return { email, phone };
}
